import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import BarekamInput from "../../../../components/input";
import { Product } from "../../../../shared/models/order/product.model";
import { store, useAppSelector } from "../../../../../redux/store";
import {
  Message,
  MessageSections,
} from "../../../../components/message/message";
import BarekamText from "../../../../components/text";
import { OrderPopupCardTitle } from "../../styled";
import {
  Alert,
  createFilterOptions,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  ListItemText,
  TextField,
  Tooltip,
} from "@mui/material";
import { BarekamSelect } from "../../../../components/select/select";
import {
  BarekamMenuItem,
  SearchFild,
  SelectedBarekamMenuItem,
} from "../../../../components/select/styled/orders";
import {
  ParcelConditionRadioGroup,
  ProductDetailsDescriptionContainer,
} from "../styled";
import BarekamRadio from "../../../../components/radio";
import { ConditionEnum } from "../../../enums/order/condition.enum";
import BarekamInputWithSelect from "../../../../components/input-with-select";
import BarekamCheckbox from "../../../../components/checkbox";
import BarekamButton, {
  ParcelEditButtons,
} from "../../../../components/button";
import {
  AttachmentType,
  BarekamAttachment,
} from "../../../../components/attachment/attachment";
import { ProductDimensions } from "./product-dimensions";
import { getMessage } from "../../../../shared/helper-functions";
import { FileModel } from "../../../../shared/models/FileModel";
import { OrderModel } from "../../../../shared/models/order/order-item.model";
import { StoreModel } from "../../../../../redux/reducers/static-data.reducers";

const StyledTextField = styled(TextField)({
  backgroundColor: "#FFF",

  "& .MuiFormLabel-root": {
    height: "3.5rem",
    width: "100%",
    display: "flex",
  },

  "& .MuiFormLabel-asterisk": {
    color: "red",
  },
});

export const ProductExpanded = ({
  index,
  order,
  product,
  validationErrors,
  onChange,
  isReadonly,
}: ProductExpandedProps) => {
  const { matchesPhone } = useAppSelector((state) => state.deviceReducer);

  const { weightUnits, currencies, productStores, productCategories } =
    useAppSelector((state) => state.staticDataReducer);

  const [textAreas, setTextAreas] = useState([""]);
  const [trackingNumber, setTrackingNumber] = useState<string>(product?.trackingNumber || '');
  const [productName, setProductName] = useState<string>(product?.name || '');

  const [isWithWeight, setIsWithWeight] = useState(!!product.weight);
  const [isWithDimensions, setIsWithDimensions] = useState(
    !!product.length && !!product.width && !!product.height,
  );
  const [filteredStores, setFilteredStores] = useState([] as StoreModel[]);

  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );

  useEffect(() => {
    const filteredStores = productStores.filter(
      (store) =>
        store.isOther ||
        (store.country?.countryId === order.shippingInfo?.country.countryId &&
          store.status === "enabled"),
    );
    setFilteredStores(filteredStores);

    if (
      !filteredStores.some(
        (store) => store.storeId === order.products[0].store?.storeId,
      )
    ) {
      // Resets product store
      onChange(undefined, "store", order.products[0]);
    }
  }, [order.shippingInfo.country]);

  useEffect(() => {
    const descriptionParts = product?.description.split("&#216");
    setTextAreas(descriptionParts);
  }, []);

  function addNewTextArea() {
    setTextAreas([...textAreas, ""]);
  }

  function onDescriptionChange(value: string, index: number) {
    setTextAreas((prevState) => {
      prevState[index] = value;

      onChange(prevState.join("&#216"), "description", product);
      return prevState;
    });
  }

  async function handleFileSelect(
    file: File | undefined,
    attachmentType: AttachmentType,
  ) {
    if (attachmentType === AttachmentType.INVOICE) {
      onChange([...(product.invoice || []), file], "invoice", product);
    } else {
      onChange([...(product.image || []), file], "image", product);
    }
  }

  function onCheckboxToggle(variant: "insertWeight" | "insertDimensions") {
    switch (variant) {
      case "insertDimensions":
        setIsWithDimensions(!isWithDimensions);
        onChange(null, "width", product);
        onChange(null, "length", product);
        onChange(null, "height", product);

        break;
      case "insertWeight":
        setIsWithWeight(!isWithWeight);
        onChange(null, "weight", product);

        break;
    }
  }

  const mappedCategories = useMemo(() => {
    const opa: any = [];
    productCategories.map((category) => {
      opa.push({
        ...category,
        name: category.name[languageId],
        description: category.description[languageId],
      });
      return opa;
    });
    return opa;
  }, [productCategories, languageId]);

  const filterOptions = createFilterOptions({
    matchFrom: "start",
    stringify: (option: any) => option?.meta_keys || option?.name,
  });

  return (
    <>
      <OrderPopupCardTitle>
        <BarekamText variant={"m"} fontWeight={600}>
          <Message
            messageKey={"new_order_product_details"}
            sectionId={MessageSections.ORDERS}
          />
        </BarekamText>
      </OrderPopupCardTitle>

      <Grid container columnSpacing={"1rem"} rowSpacing={"1rem"}>
        <Grid item xs={10}>
          <BarekamInput
            id={"tracking_number-" + index}
            value={trackingNumber}
            placeholder={getMessage(
              "tracking_number",
              MessageSections.ORDERS,
              messageCache,
              languageId,
            )}
            customInputVariant={"standard"}
            width={"100%"}
            onChange={(event) => setTrackingNumber(event.target.value)}
            onBlur={(event) =>
              onChange(event.target.value, "trackingNumber", product)}
            error={!!validationErrors["tracking_number"]}
            errorMessage={
              validationErrors["tracking_number"] &&
              getMessage(
                validationErrors["tracking_number"],
                MessageSections.ORDERS,
                messageCache,
                languageId,
              )
            }
            isReadonly={isReadonly}
            required
          />
        </Grid>

        <Grid item xs={2}>
          <BarekamInput
            value={product.otp}
            placeholder={getMessage(
              "otp",
              MessageSections.ORDERS,
              messageCache,
              languageId,
            )}
            customInputVariant={"standard"}
            width={"100%"}
            isReadonly={isReadonly}
            onChange={(event) => onChange(event.target.value, "otp", product)}
          />
        </Grid>

        <Grid item xs={12}>
          <BarekamInput
            id={"product_name-" + index}
            value={productName}
            placeholder={getMessage(
              "product_name",
              MessageSections.ORDERS,
              messageCache,
              languageId,
            )}
            customInputVariant={"standard"}
            width={"100%"}
            onChange={(event) => setProductName(event.target.value)}
            onBlur={(event) => onChange(event.target.value, "name", product)}
            error={!!validationErrors["product_name"]}
            errorMessage={
              validationErrors["product_name"] &&
              getMessage(
                validationErrors["product_name"],
                MessageSections.ORDERS,
                messageCache,
                languageId,
              )
            }
            isReadonly={isReadonly}
            required
            validatePattern
          />
        </Grid>

        {/* <Grid item xs={12}>
          <FormControl error={!!validationErrors["category"]} fullWidth>
            <SearchFild
              options={mappedCategories}
              getOptionLabel={(option: any) => option.name}
              filterOptions={filterOptions}
              renderOption={(props, option: any) => {
                const { key, ...optionProps } = props;
                return (
                  <BarekamMenuItem {...optionProps}>
                    <ListItemText
                      style={{ margin: "unset" }}
                      primary={option.name}
                      secondary={option.description}
                      onClick={() => onChange?.(option, "category", product)}
                    />
                  </BarekamMenuItem>
                );
              }}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  required
                  error={!!validationErrors["category"]}
                  label={
                    <BarekamText variant={"1rem"}>
                      <Message
                        messageKey={"product_category"}
                        sectionId={MessageSections.ORDERS}
                      />
                    </BarekamText>
                  }
                />
              )}
            />
            {!!validationErrors["category"] ? (
              <FormHelperText>
                {validationErrors["category"] &&
                  getMessage(
                    validationErrors["category"],
                    MessageSections.ORDERS,
                    messageCache,
                    languageId,
                  )}
              </FormHelperText>
            ) : (
              <></>
            )}
          </FormControl>
        </Grid> */}

        <Grid item xs={12}>
          <BarekamSelect
            id={"product_store-" + index}
            variant={"outlined"}
            label={
              <BarekamText variant={"1rem"}>
                <Message
                  messageKey={"product_store"}
                  sectionId={MessageSections.ORDERS}
                />
              </BarekamText>
            }
            value={
              (!!filteredStores.length &&
                product?.store &&
                product?.store?.storeId) ||
              ""
            }
            type={"standard"}
            error={!!validationErrors["store"]}
            errorMessage={
              validationErrors["store"] &&
              getMessage(
                validationErrors["store"],
                MessageSections.ORDERS,
                messageCache,
                languageId,
              )
            }
            disabled={isReadonly}
            fullWidth
            required
          >
            {filteredStores.map((store) => {
              if (store.status === "disabled") return null;
              if (product?.store && product?.store?.storeId === store.storeId) {
                return (
                  <SelectedBarekamMenuItem
                    key={store.storeId}
                    value={store.storeId}
                    onClick={() => onChange(store, "store", product)}
                  >
                    <ListItemText
                      style={{ margin: "unset" }}
                      primary={store.name && store.name[languageId]}
                    />
                  </SelectedBarekamMenuItem>
                );
              } else {
                return (
                  <BarekamMenuItem
                    key={store.storeId}
                    value={store.storeId}
                    onClick={() => onChange(store, "store", product)}
                  >
                    <ListItemText
                      style={{ margin: "unset" }}
                      primary={store.name && store.name[languageId]}
                    />
                  </BarekamMenuItem>
                );
              }
            })}
          </BarekamSelect>
        </Grid>

        {product.store?.isOther && (
          <Grid item xs={12}>
            <BarekamInput
              id={"storeName-" + index}
              value={product.storeName}
              placeholder={getMessage(
                "store",
                MessageSections.ORDERS,
                messageCache,
                languageId,
              )}
              customInputVariant={"standard"}
              width={"100%"}
              isReadonly={isReadonly}
              error={!!validationErrors["storeName"]}
              errorMessage={
                validationErrors["storeName"] &&
                getMessage(
                  validationErrors["storeName"],
                  MessageSections.ORDERS,
                  messageCache,
                  languageId,
                )
              }
              onChange={(event) =>
                onChange(event.target.value, "storeName", product)
              }
              required
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <ParcelConditionRadioGroup
            row
            name="product_condition"
            defaultValue={0}
            value={product.condition}
            onChange={(event) =>
              onChange(event.target.value, "condition", product)
            }
          >
            <FormControlLabel
              control={<BarekamRadio />}
              label={
                <BarekamText variant={"s"}>
                  <Message
                    messageKey={"new"}
                    sectionId={MessageSections.ORDERS}
                  />
                </BarekamText>
              }
              onChange={() => onChange(ConditionEnum.NEW, "condition", product)}
              value={ConditionEnum.NEW}
              disabled={isReadonly}
              labelPlacement={"end"}
            />

            <FormControlLabel
              control={<BarekamRadio />}
              label={
                <BarekamText variant={"s"}>
                  <Message
                    messageKey={"used"}
                    sectionId={MessageSections.ORDERS}
                  />
                </BarekamText>
              }
              onChange={() =>
                onChange(ConditionEnum.USED, "condition", product)
              }
              value={ConditionEnum.USED}
              disabled={isReadonly}
              labelPlacement={"end"}
            />
          </ParcelConditionRadioGroup>
        </Grid>

        <Grid item xs={matchesPhone ? 12 : 6}>
          <BarekamInputWithSelect
            id={"price-" + index}
            types={currencies}
            selectedType={product.currency?.currencyId || 1}
            selectedValue={product.price}
            onValueChange={(value) => onChange(value, "price", product)}
            onTypeChange={(value) => onChange(value, "currency", product)}
            idFieldName={"currencyId"}
            error={!!validationErrors["price"]}
            errorMessage={
              validationErrors["price"] &&
              getMessage(
                validationErrors["price"],
                MessageSections.ORDERS,
                messageCache,
                languageId,
              )
            }
            isReadonly={isReadonly}
            placeholderKey={"price"}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <Alert severity="info">
            <BarekamText variant={"m"}>
              <Message
                messageKey={"product_weight_dimensions_info"}
                sectionId={MessageSections.ORDERS}
              />
            </BarekamText>
          </Alert>
        </Grid>

        {isReadonly ? (
          ""
        ) : (
          <Grid item xs={12}>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <BarekamCheckbox
                onClick={() => onCheckboxToggle("insertWeight")}
                checked={isWithWeight}
              />
              <BarekamText variant={"s"}>
                <Message
                  messageKey={"insert_weight"}
                  sectionId={MessageSections.ORDERS}
                />
              </BarekamText>
            </div>
          </Grid>
        )}

        {isWithWeight ? (
          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamInputWithSelect
              types={weightUnits}
              selectedType={product.weightUnit?.weightUnitId || 1}
              selectedValue={product.weight}
              onValueChange={(value) => onChange(value, "weight", product)}
              onTypeChange={(value) => onChange(value, "weightUnit", product)}
              isReadonly={isReadonly}
              placeholderKey={"weight"}
              idFieldName={"weightUnitId"}
            />
          </Grid>
        ) : (
          ""
        )}

        {isReadonly ? (
          ""
        ) : (
          <Grid item xs={12}>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <BarekamCheckbox
                onClick={() => onCheckboxToggle("insertDimensions")}
                checked={isWithDimensions}
              />
              <BarekamText variant={"s"}>
                <Message
                  messageKey={"insert_dimensions"}
                  sectionId={MessageSections.ORDERS}
                />
              </BarekamText>
            </div>
          </Grid>
        )}

        {isWithDimensions ? (
          <Grid item xs={12}>
            <ProductDimensions
              product={product}
              errors={validationErrors}
              isReadonly={isReadonly}
              handleChange={onChange}
            />
          </Grid>
        ) : (
          ""
        )}

        <Grid item xs={12}>
          <ProductDetailsDescriptionContainer>
            {textAreas.map((text, index) => (
              <BarekamInput
                value={text}
                placeholder={getMessage(
                  "description",
                  MessageSections.ORDERS,
                  messageCache,
                  languageId,
                )}
                customInputVariant={"standard"}
                width={"100%"}
                key={index}
                isReadonly={isReadonly}
                multiline
                onChange={(event) =>
                  onDescriptionChange(event.target.value, index)
                }
              />
            ))}
          </ProductDetailsDescriptionContainer>
        </Grid>

        {!isReadonly && (
          <Grid item xs={12} paddingLeft={"1rem"}>
            <BarekamButton
              type={ParcelEditButtons.ADD_DESCRIPTION}
              size={"s"}
              onClick={addNewTextArea}
            >
              <Tooltip
                title={getMessage(
                  "add_new_description",
                  MessageSections.ORDERS,
                  messageCache,
                  languageId,
                )}
              >
                <div>
                  <BarekamText variant={"xxl"}> + </BarekamText>
                </div>
              </Tooltip>
            </BarekamButton>
          </Grid>
        )}

        {!isReadonly || product?.invoice.length ? (
          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamAttachment
              titleLabel={
                <Message
                  messageKey={"invoice"}
                  sectionId={MessageSections.ORDERS}
                />
              }
              values={product.invoice}
              isSingle={false}
              isReadonly={isReadonly}
              onValuesChange={(values: (File | FileModel)[]) =>
                onChange(values, "invoice", product)
              }
              onValueChange={(data: File) =>
                handleFileSelect(data, AttachmentType.INVOICE)
              }
            />
          </Grid>
        ) : (
          <></>
        )}

        {!isReadonly || product?.image.length ? (
          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamAttachment
              titleLabel={
                <Message
                  messageKey={"image"}
                  sectionId={MessageSections.ORDERS}
                />
              }
              values={product.image}
              isSingle={false}
              isReadonly={isReadonly}
              onValuesChange={(values: (File | FileModel)[]) =>
                onChange(values, "image", product)
              }
              onValueChange={(data: File) =>
                handleFileSelect(data, AttachmentType.IMAGE)
              }
            />
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    </>
  );
};

interface ProductExpandedProps {
  index: number;
  order: OrderModel;
  product: Product;
  validationErrors: { [key: string]: string };
  isReadonly: boolean;
  onChange: (value: any, fieldName: string, product: Product) => void;
}
